import React, { useEffect, useRef, useState } from 'react';
type ScaledViewportProps = {
  /** Native design size of the content, e.g. a 390 × 760 phone screen. */
  baseWidth: number;
  baseHeight: number;
  /** Bezel thickness (px) drawn around the screen. */
  bezel?: number;
  /** Corner radius of the device as a fraction of its rendered width. */
  radius?: number;
  /** Static still: not focusable or clickable, announced as one image. */
  still?: boolean;
  label?: string;
  /** Sizes the measuring box. The device is fitted (contain) and centered inside it. */
  className?: string;
  style?: React.CSSProperties;
  deviceClassName?: string;
  children: React.ReactNode;
};

/** Renders fixed-size content scaled to fit its box, so a 390px phone UI keeps its true mobile layout. */
export function ScaledViewport({
  baseWidth,
  baseHeight,
  bezel = 0,
  radius = 0.11,
  still = false,
  label,
  className = '',
  style,
  deviceClassName = '',
  children
}: ScaledViewportProps) {
  const outer = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    const el = outer.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth - bezel * 2;
      const h = el.clientHeight - bezel * 2;
      const s = Math.min(w / baseWidth, h / baseHeight);
      setScale(Number.isFinite(s) && s > 0 ? s : 0);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [baseWidth, baseHeight, bezel]);
  useEffect(() => {
    if (still) inner.current?.setAttribute('inert', '');
  }, [still]);
  const deviceW = baseWidth * scale + bezel * 2;
  return <div ref={outer} role={still ? 'img' : undefined} aria-label={still ? label : undefined} className={`relative flex items-center justify-center ${className}`} style={style} data-id="element-322" data-mp="2ufk11mnv9k9b">
      <div className={`relative overflow-hidden ${deviceClassName}`} style={{
      width: deviceW,
      height: baseHeight * scale + bezel * 2,
      padding: bezel,
      borderRadius: deviceW * radius,
      visibility: scale ? 'visible' : 'hidden'
    }} data-id="element-323" data-mp="1j0x17y4wky6l">
        <div className="relative h-full w-full overflow-hidden" style={{
        borderRadius: Math.max(0, deviceW * radius - bezel)
      }} data-id="element-324" data-mp="3ayy64ss7eswp">
          <div ref={inner} aria-hidden={still || undefined} className={`absolute left-0 top-0 ${still ? 'pointer-events-none select-none' : ''}`} style={{
          width: baseWidth,
          height: baseHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left'
        }} data-id="element-325" data-mp="36y5vpw7lihyu">
            {children}
          </div>
        </div>
      </div>
    </div>;
}